import type { Task } from '@medplum/fhirtypes';
import React from 'react';

import {
  CLINICAL_TASK_STATUS_LABELS,
  asClinicalTaskStatus,
  categoryLabel,
  readTaskCategory,
  readTaskDueDate,
  taskDueState,
} from '@eluve/clinical-tasks';
import { Checkbox, Icon, Stack, Tag, Text, cn } from '@eluve/components';
import {
  type FhirPageControls,
  type FhirSectionStatus,
  LoadMoreRow,
  SectionBoundary,
} from '@eluve/fhir-data';
import { formatFhirDate } from '@eluve/fhir-renderer';

/**
 * The Overview-tab My-tasks card (ENG-5368): the signed-in clinician's
 * accepted Clinical Tasks for this patient, checkbox complete/undo on the
 * row (same requested ⇄ completed semantics as the appointment-side panel),
 * due-today/past-due badging, rows clickable into the detail drawer.
 */
export type TasksCardProps = {
  tasks: Task[];
  status: FhirSectionStatus;
  paging: FhirPageControls;
  /** The clock due-state badges derive from — a prop so stories stay stable. */
  now: Date;
  /** Ids of tasks whose complete/undo writes are in flight. */
  togglingIds: ReadonlySet<string>;
  onToggle: (taskId: string, done: boolean) => void;
  onOpen: (taskId: string) => void;
};

const TaskRow: React.FC<{
  task: Task;
  now: Date;
  toggling: boolean;
  onToggle: (done: boolean) => void;
  onOpen: () => void;
}> = ({ task, now, toggling, onToggle, onOpen }) => {
  const done = task.status === 'completed';
  const dueDate = readTaskDueDate(task);
  const dueState = done ? null : taskDueState(dueDate, now);
  const category = readTaskCategory(task);

  return (
    <div
      data-testid="my-task-row"
      className="relative flex w-full items-start pl-3 not-first:before:pointer-events-none not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
    >
      <Checkbox
        checked={done}
        disabled={toggling}
        onCheckedChange={(next) => onToggle(next === true)}
        aria-label={done ? 'Reopen task' : 'Complete task'}
        className="mt-3"
      />
      <button
        type="button"
        onClick={onOpen}
        className="ml-1.5 flex min-w-0 flex-1 items-center gap-3 rounded-sm py-2.5 pr-3 pl-1 text-left hover:bg-bg-secondary"
      >
        <div className="min-w-0 flex-1">
          <Stack
            direction="horizontal"
            gap={2}
            align="baseline"
            fullWidth={false}
            className="min-h-6"
          >
            <Text
              inline
              size="sm"
              weight="medium"
              color={done ? 'tertiary' : undefined}
              className={cn('truncate privacy-text', done && 'line-through')}
            >
              {task.description ?? '(no description)'}
            </Text>
            {dueState === 'due-today' && (
              <Tag pill subtle size="xs" color="warning" label="Due today" />
            )}
            {dueState === 'past-due' && (
              <Tag pill subtle size="xs" color="negative" label="Past due" />
            )}
          </Stack>
          <Text
            size="xs"
            color="tertiary"
            weight="medium"
            className="mt-0.5 privacy-text"
          >
            {[
              CLINICAL_TASK_STATUS_LABELS[
                asClinicalTaskStatus(task.status) ?? 'requested'
              ],
              dueDate ? `Due: ${formatFhirDate(dueDate)}` : undefined,
              category ? categoryLabel(category) : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
        <Icon
          name="ChevronRight"
          size="xs"
          className="shrink-0 text-text-tertiary"
        />
      </button>
    </div>
  );
};

export const TasksCard: React.FC<TasksCardProps> = ({
  tasks,
  status,
  paging,
  now,
  togglingIds,
  onToggle,
  onOpen,
}) => (
  <section
    data-testid="my-tasks-card"
    className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
  >
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      justify="between"
      className="h-12 px-4"
    >
      <Text inline size="sm" weight="semibold">
        My Tasks
      </Text>
    </Stack>
    <div className="px-4">
      <SectionBoundary status={status} emptyText="No tasks for this patient.">
        <div className="-mx-3 py-1">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              now={now}
              toggling={Boolean(task.id && togglingIds.has(task.id))}
              onToggle={(done) => task.id && onToggle(task.id, done)}
              onOpen={() => task.id && onOpen(task.id)}
            />
          ))}
          <LoadMoreRow paging={paging} noun="tasks" />
        </div>
      </SectionBoundary>
    </div>
  </section>
);
